<div class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50" (click)="editorClose.emit()">
  <div
    class="flex h-[85vh] w-[90vw] max-w-[1400px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
    (click)="$event.stopPropagation()"
  >
    <!-- Header + Toolbar combined -->
    <div class="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-4 py-2">
      <div class="flex items-center gap-4">
        <h2 class="text-sm font-semibold text-neutral-700">SuperLatex Editor</h2>
        <div class="h-4 w-px bg-neutral-300"></div>
        <div class="flex items-center gap-2">
          <dojo-button
            [size]="'s'"
            [fill]="mode() === 'create-anchor' ? 'solid' : 'outline'"
            (click)="startCreateAnchor()"
          >
            <dojo-icon icon="plus" [size]="14" class="mr-1" />
            Create Anchor
          </dojo-button>
          <dojo-button
            [size]="'s'"
            [fill]="mode() === 'select-anchor' ? 'solid' : 'outline'"
            (click)="startSelectAnchors()"
          >
            Select Anchors
          </dojo-button>
          <dojo-button
            [size]="'s'"
            [fill]="mode() === 'connect-from' || mode() === 'connect-to' ? 'solid' : 'outline'"
            (click)="startCreateConnection()"
          >
            Connect
          </dojo-button>
          @if (mode() !== 'view') {
            <dojo-button [size]="'s'" [fill]="'outline'" (click)="cancelMode()"> Cancel </dojo-button>
          }
        </div>
      </div>

      <div class="flex items-center gap-2">
        @if (selectedAnchors().length > 0) {
          <dojo-button [size]="'s'" [fill]="'outline'" (click)="copyAnchorsToClipboard()">
            Copy {{ selectedAnchors().length }} Anchor(s)
          </dojo-button>
        }
        @if (connections().length > 0) {
          <dojo-button [size]="'s'" [fill]="'outline'" (click)="copyConnectionsToClipboard()">
            Copy Connections
          </dojo-button>
        }
        <button
          class="flex h-7 w-7 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600"
          (click)="editorClose.emit()"
        >
          <dojo-icon icon="xmark" [size]="16" />
        </button>
      </div>
    </div>

    <!-- Mode indicator -->
    @if (mode() !== 'view') {
      <div class="flex items-center gap-2 bg-amber-50 px-4 py-1.5 text-xs text-amber-700">
        <dojo-icon icon="info-circle" [size]="14" />
        <span>{{ getModeLabel() }}</span>
        @if (mode() === 'connect-to' && connectFromAnchor()) {
          <span class="rounded bg-blue-500 px-2 py-0.5 text-xs font-medium text-white">
            From: {{ connectFromAnchor() }}
          </span>
        }
      </div>
    }

    <!-- Main content area -->
    <div class="flex flex-1 overflow-hidden">
      <!-- LaTeX Preview Panel -->
      <div
        class="relative flex-1 overflow-auto p-8"
        #latexContainer
        [class.cursor-crosshair]="mode() !== 'view'"
        [class.select-none]="mode() !== 'view'"
        (mousedown)="onLatexMouseDown($event)"
        (mousemove)="onLatexMouseMove($event); onLatexMouseMoveLasso($event)"
        (mouseup)="onLatexMouseUp($event)"
        (mouseleave)="onLatexMouseUp($event)"
        (click)="onLatexClick($event)"
        (wheel)="onLatexWheel($event)"
      >
        @for (node of content().latex_nodes; track node.id ?? node.latex) {
          <div class="latex-line mb-8">
            <markdown [data]="node.latex" katex [katexOptions]="katexOptions" />
          </div>
        }

        <!-- Hover highlight overlay for existing anchors -->
        @if (hoveredAnchor(); as hovered) {
          <div
            class="pointer-events-none absolute z-10 rounded border-2 border-blue-500 bg-blue-500/20"
            [style.left.px]="getRelativeLeft(hovered.rect)"
            [style.top.px]="getRelativeTop(hovered.rect)"
            [style.width.px]="hovered.rect.width"
            [style.height.px]="hovered.rect.height"
          >
            <span class="absolute -top-7 left-0 whitespace-nowrap rounded bg-blue-500 px-2 py-1 text-xs text-white">
              {{ hovered.id }}
            </span>
          </div>
        }

        <!-- Hover highlight for create-anchor mode (non-anchor elements) -->
        @if (mode() === 'create-anchor' && hoveredElement(); as hovered) {
          @if (!hovered.isExistingAnchor) {
            <div
              class="pointer-events-none absolute z-10 rounded border-2 border-dashed border-emerald-500 bg-emerald-500/20"
              [style.left.px]="getRelativeLeft(hovered.rect)"
              [style.top.px]="getRelativeTop(hovered.rect)"
              [style.width.px]="hovered.rect.width"
              [style.height.px]="hovered.rect.height"
            >
              <span
                class="absolute -top-7 left-0 flex items-center gap-2 whitespace-nowrap rounded bg-emerald-500 px-2 py-1 text-xs text-white"
              >
                "{{ hovered.text }}"
                @if (hoverableElements().length > 1) {
                  <span class="opacity-75">(scroll: {{ hoverLevel() + 1 }}/{{ hoverableElements().length }})</span>
                }
              </span>
            </div>
          }
        }

        <!-- Selected anchors overlay -->
        @for (anchorId of selectedAnchors(); track anchorId) {
          @if (getAnchorRect(anchorId); as rect) {
            <div
              class="pointer-events-none absolute z-10 rounded border-2 border-emerald-500 bg-emerald-500/20"
              [style.left.px]="getRelativeLeft(rect)"
              [style.top.px]="getRelativeTop(rect)"
              [style.width.px]="rect.width"
              [style.height.px]="rect.height"
            ></div>
          }
        }

        <!-- Lasso selection rectangle -->
        @if (getLassoRect(); as rect) {
          <div
            class="pointer-events-none absolute z-20 border-2 border-dashed border-violet-500 bg-violet-500/10"
            [style.left.px]="rect.left"
            [style.top.px]="rect.top"
            [style.width.px]="rect.width"
            [style.height.px]="rect.height"
          ></div>
        }

        <!-- Lasso selection highlight -->
        @if (lassoSelection(); as selection) {
          <div
            class="pointer-events-none absolute z-10 rounded border-2 border-violet-500 bg-violet-500/20"
            [style.left.px]="getRelativeLeft(selection.combinedRect)"
            [style.top.px]="getRelativeTop(selection.combinedRect)"
            [style.width.px]="selection.combinedRect.width"
            [style.height.px]="selection.combinedRect.height"
          >
            <span
              class="absolute -top-7 left-0 max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap rounded bg-violet-500 px-2 py-1 text-xs text-white"
            >
              {{ selection.texts.join('') }}
            </span>
          </div>
        }
      </div>

      <!-- Side Panel -->
      <div class="w-80 overflow-y-auto border-l border-neutral-200 bg-neutral-50">
        <!-- Anchors List -->
        <div class="border-b border-neutral-200 p-4">
          <h3 class="mb-3 text-sm font-semibold text-neutral-700">Existing Anchors ({{ existingAnchors().length }})</h3>
          <div class="flex max-h-48 flex-col gap-1 overflow-y-auto">
            @for (anchor of existingAnchors(); track anchor.id) {
              <div
                class="flex items-center justify-between rounded border bg-white px-3 py-2 text-xs"
                [class.border-emerald-500]="selectedAnchors().includes(anchor.id)"
                [class.bg-emerald-50]="selectedAnchors().includes(anchor.id)"
                [class.border-neutral-200]="!selectedAnchors().includes(anchor.id)"
              >
                <code class="font-mono text-neutral-700">{{ anchor.id }}</code>
                <button
                  class="text-neutral-400 transition-colors hover:text-red-500"
                  (click)="deleteAnchor(anchor.id)"
                  title="Delete anchor"
                >
                  <dojo-icon icon="trash" [size]="14" />
                </button>
              </div>
            }
            @if (existingAnchors().length === 0) {
              <p class="py-4 text-center text-xs text-neutral-400">No anchors yet</p>
            }
          </div>
        </div>

        <!-- Connections List -->
        <div class="border-b border-neutral-200 p-4">
          <h3 class="mb-3 text-sm font-semibold text-neutral-700">Connections ({{ connections().length }})</h3>
          <div class="flex max-h-48 flex-col gap-1 overflow-y-auto">
            @for (conn of connections(); track conn.id) {
              <div
                class="flex cursor-pointer items-center gap-2 rounded border bg-white px-3 py-2 text-xs transition-colors hover:border-blue-300"
                [class.border-blue-500]="selectedConnection()?.id === conn.id"
                [class.bg-blue-50]="selectedConnection()?.id === conn.id"
                [class.border-neutral-200]="selectedConnection()?.id !== conn.id"
                (click)="editConnection(conn)"
              >
                <code class="font-mono text-indigo-600">{{ conn.from_anchor }}</code>
                <dojo-icon icon="arrow-right" [size]="12" class="text-neutral-400" />
                <code class="font-mono text-indigo-600">{{ conn.to_anchor }}</code>
              </div>
            }
            @if (connections().length === 0) {
              <p class="py-4 text-center text-xs text-neutral-400">No connections yet</p>
            }
          </div>
        </div>

        <!-- Connection Editor -->
        @if (editingConnection(); as conn) {
          <div class="p-4">
            <h3 class="mb-3 text-sm font-semibold text-neutral-700">Edit Connection</h3>

            <div class="space-y-2">
              <ion-item class="rounded-lg border border-neutral-200">
                <ion-select
                  label="Color"
                  labelPlacement="stacked"
                  [value]="conn.style?.color ?? 'neutral'"
                  (ionChange)="updateConnectionStyle('color', $any($event).detail.value)"
                >
                  @for (color of colorOptions; track color) {
                    <ion-select-option [value]="color">{{ color }}</ion-select-option>
                  }
                </ion-select>
              </ion-item>

              <ion-item class="rounded-lg border border-neutral-200">
                <ion-input
                  label="Line Width"
                  labelPlacement="stacked"
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="5"
                  [value]="conn.style?.width ?? 1.2"
                  (ionInput)="updateConnectionStyle('width', +$any($event).detail.value)"
                />
              </ion-item>

              <ion-item class="rounded-lg border border-neutral-200">
                <ion-select
                  label="Line Style"
                  labelPlacement="stacked"
                  [value]="conn.style?.line_style ?? 'solid'"
                  (ionChange)="updateConnectionStyle('line_style', $any($event).detail.value)"
                >
                  <ion-select-option value="solid">Solid</ion-select-option>
                  <ion-select-option value="dashed">Dashed</ion-select-option>
                  <ion-select-option value="dotted">Dotted</ion-select-option>
                </ion-select>
              </ion-item>

              <ion-item class="rounded-lg border border-neutral-200">
                <ion-select
                  label="Arrow Style"
                  labelPlacement="stacked"
                  [value]="conn.style?.arrow_style ?? 'open'"
                  (ionChange)="updateConnectionStyle('arrow_style', $any($event).detail.value)"
                >
                  <ion-select-option value="none">None</ion-select-option>
                  <ion-select-option value="open">Open</ion-select-option>
                  <ion-select-option value="filled">Filled</ion-select-option>
                </ion-select>
              </ion-item>

              <div class="grid grid-cols-2 gap-2">
                <ion-item class="rounded-lg border border-neutral-200">
                  <ion-select
                    label="From"
                    labelPlacement="stacked"
                    [value]="conn.style?.from_socket ?? 'Bottom'"
                    (ionChange)="updateConnectionStyle('from_socket', $any($event).detail.value)"
                  >
                    <ion-select-option value="Top">Top</ion-select-option>
                    <ion-select-option value="Bottom">Bottom</ion-select-option>
                    <ion-select-option value="Left">Left</ion-select-option>
                    <ion-select-option value="Right">Right</ion-select-option>
                  </ion-select>
                </ion-item>
                <ion-item class="rounded-lg border border-neutral-200">
                  <ion-select
                    label="To"
                    labelPlacement="stacked"
                    [value]="conn.style?.to_socket ?? 'Top'"
                    (ionChange)="updateConnectionStyle('to_socket', $any($event).detail.value)"
                  >
                    <ion-select-option value="Top">Top</ion-select-option>
                    <ion-select-option value="Bottom">Bottom</ion-select-option>
                    <ion-select-option value="Left">Left</ion-select-option>
                    <ion-select-option value="Right">Right</ion-select-option>
                  </ion-select>
                </ion-item>
              </div>

              <ion-item class="rounded-lg border border-neutral-200">
                <ion-input
                  label="Stub Length"
                  labelPlacement="stacked"
                  type="number"
                  min="0"
                  max="50"
                  [value]="conn.style?.stub ?? 12"
                  (ionInput)="updateConnectionStyle('stub', +$any($event).detail.value)"
                />
              </ion-item>

              <div class="flex gap-2 pt-2">
                <dojo-button [size]="'s'" [fill]="'solid'" (click)="saveConnectionEdit()" class="flex-1">
                  Save
                </dojo-button>
                <dojo-button [size]="'s'" [fill]="'outline'" (click)="cancelConnectionEdit()"> Cancel </dojo-button>
                <dojo-button [size]="'s'" [fill]="'outline'" (click)="deleteConnection(conn.id)" class="text-red-500">
                  <dojo-icon icon="trash" [size]="14" />
                </dojo-button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</div>

<!-- Anchor Creation Dialog -->
@if (showAnchorDialog()) {
  <div
    class="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50"
    (click)="closeAnchorDialog()"
    (mousedown)="$event.stopPropagation()"
    (mousemove)="$event.stopPropagation()"
    (mouseup)="$event.stopPropagation()"
  >
    <div
      class="w-96 rounded-xl bg-white p-6 shadow-2xl"
      (click)="$event.stopPropagation()"
      (mousedown)="$event.stopPropagation()"
    >
      <h3 class="mb-4 text-lg font-semibold text-neutral-900">Create Anchor</h3>
      <p class="mb-4 text-sm text-neutral-500">
        Creating anchor for: <strong class="text-neutral-700">"{{ pendingAnchor()?.text }}"</strong>
      </p>
      <ion-item class="mb-4 rounded-lg border border-neutral-200">
        <ion-input
          label="Anchor ID"
          labelPlacement="stacked"
          [value]="newAnchorId()"
          (ionInput)="newAnchorId.set($any($event).detail.value)"
          placeholder="e.g., term-x, equation-1"
          (keydown.enter)="confirmCreateAnchor()"
          class="font-mono"
        />
      </ion-item>
      <div class="flex gap-2">
        <dojo-button [size]="'s'" [fill]="'solid'" (click)="confirmCreateAnchor()" class="flex-1"> Create </dojo-button>
        <dojo-button [size]="'s'" [fill]="'outline'" (click)="closeAnchorDialog()"> Cancel </dojo-button>
      </div>
    </div>
  </div>
}
